import { useEffect, useState } from "react";
import { BusyLabel } from "@/components/loading";
import { api } from "@/lib/api";
import { useGame, GAMES, useActiveGames } from "@/lib/game";
import { GameIcon } from "@/components/GameIcon";
import { Button } from "@/components/ui/button";
import { Save, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Tarjeta para gestionar preferencias de juego desde el perfil del usuario
 * (BuyerDashboard y SellerDashboard). Permite:
 *   - Cambiar su juego principal (el que ve por defecto al entrar a HitTCG).
 *   - Ajustar los juegos de interés — incluidos los "próximamente" para que
 *     el admin vea qué priorizar.
 *
 * Sincroniza con `useGame().activeGame`: al cambiar el principal, también
 * cambia la vista actual del usuario (`setActiveGame(primary)`) para que
 * la Home/Catálogo se recarguen con el nuevo default.
 */
export default function GamePreferencesCard() {
  const { prefs, refreshPrefs, setActiveGame } = useGame();
  const [interests, setInterests] = useState({});
  const [primary, setPrimary] = useState("pokemon");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const activeGames = useActiveGames();
  const isActive = (k) => activeGames.some((g) => g.key === k);

  useEffect(() => {
    if (!prefs) return;
    const next = {};
    Object.keys(GAMES).forEach((k) => {
      next[k] = (prefs.games_of_interest || ["pokemon"]).includes(k);
    });
    setInterests(next);
    setPrimary(prefs.primary_game || "pokemon");
    setDirty(false);
  }, [prefs]);

  const toggle = (k) => {
    setInterests((s) => ({ ...s, [k]: !s[k] }));
    setDirty(true);
  };
  const choosePrimary = (k) => {
    // Al elegir un principal, aseguramos que también quede marcado como interés.
    setInterests((s) => ({ ...s, [k]: true }));
    setPrimary(k);
    setDirty(true);
  };

  const save = async () => {
    const games_of_interest = Object.entries(interests).filter(([, v]) => v).map(([k]) => k);
    if (games_of_interest.length === 0) return toast.error("Marca al menos un juego que te interese");
    if (!isActive(primary)) return toast.error("Tu juego principal debe ser un juego disponible");
    if (!interests[primary]) return toast.error("Tu juego principal debe estar entre tus intereses");
    setBusy(true);
    try {
      await api.put("/me/game-preferences", { games_of_interest, primary_game: primary });
      setActiveGame(primary);
      await refreshPrefs();
      toast.success("Preferencias de juego actualizadas");
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudieron guardar");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#111] border border-[#262626] rounded-md p-4 sm:p-6 space-y-4" data-testid="profile-game-prefs-card">
      <div>
        <h3 className="font-display text-lg text-white">Preferencia de juegos</h3>
        <p className="text-xs text-neutral-500 mt-1">
          Elige qué juegos te interesan y cuál quieres como <b>principal</b> — es el que verás por defecto al entrar a HIT TCG.
        </p>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-2">Juego principal</div>
        <div className="grid grid-cols-2 gap-2" data-testid="profile-game-primary-choice">
          {activeGames.map((g) => (
            <button
              key={g.key}
              type="button"
              data-testid={`profile-game-primary-${g.key}`}
              onClick={() => choosePrimary(g.key)}
              className={`p-3 rounded-md border-2 text-left transition-colors ${primary === g.key ? "" : "border-[#262626] bg-[#0A0A0A] hover:border-neutral-600"}`}
              style={primary === g.key ? { borderColor: g.color, backgroundColor: `${g.color}1a` } : undefined}
            >
              <div className="flex items-center gap-2">
                <GameIcon game={g.key} size="md" />
                <span className="text-white font-medium text-sm">{g.label}</span>
                {primary === g.key && <Check size={14} className="ml-auto" style={{ color: g.color }} />}
              </div>
              <div className="text-[10px] text-neutral-500 mt-1">{primary === g.key ? "Principal" : "Elegir"}</div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-2">Juegos de interés</div>
        <div className="space-y-2" data-testid="profile-game-interests">
          {Object.values(GAMES).map((g) => (
            <label
              key={g.key}
              data-testid={`profile-game-interest-${g.key}`}
              className={`flex items-center gap-3 p-2.5 rounded-md border cursor-pointer transition-colors ${interests[g.key] ? "border-[#EF4444]/60 bg-[#EF4444]/5" : "border-[#262626] bg-[#0A0A0A] hover:border-neutral-600"}`}
            >
              <input
                type="checkbox"
                checked={!!interests[g.key]}
                onChange={() => toggle(g.key)}
                className="w-4 h-4 accent-[#EF4444]"
              />
              <GameIcon game={g.key} size="md" />
              <span className="flex-1 text-white text-sm">{g.label}</span>
              {!isActive(g.key) && (
                <span className="text-[9px] uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">
                  Próximamente
                </span>
              )}
            </label>
          ))}
        </div>
      </div>

      <div>
        <Button
          type="button"
          data-testid="profile-game-save"
          onClick={save}
          disabled={busy || !dirty}
          className="bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-50"
        >
          <Save size={14} className="mr-2" />
          <BusyLabel busy={busy} text="Guardando…">{dirty ? "Guardar cambios" : "Sin cambios"}</BusyLabel>
        </Button>
      </div>
    </div>
  );
}
